import React, { useMemo, useState } from 'react';
import {
  UCAT_SECTION_CONFIG,
  UCAT_SJT_MAX_RAW,
  UCAT_TOTAL_MAX,
  estimateSjtBand,
  estimateTotalScore,
} from '../utils/ucatScoreEstimator';
import './UcatScoreCalculator.css';

const SECTION_KEYS = ['vr', 'dm', 'qr'];
const DM_HELP_ID = 'ucat-score-dm-help';
const DM_HELP =
  'Decision Making can award two marks for a fully correct multiple-statement item and one mark for a partially correct response.';

const DEFAULT_RAW = { vr: 24, dm: 27, qr: 24, sjt: 42 };

const INPUT_ROWS = [
  { key: 'vr', label: 'Verbal Reasoning', maxRaw: 44 },
  { key: 'dm', label: 'Decision Making', maxRaw: 47, helpId: DM_HELP_ID, help: DM_HELP },
  { key: 'qr', label: 'Quantitative Reasoning', maxRaw: 36 },
  { key: 'sjt', label: 'Situational Judgement', maxRaw: UCAT_SJT_MAX_RAW },
];

function maxFor(key) {
  return key === 'sjt' ? UCAT_SJT_MAX_RAW : UCAT_SECTION_CONFIG[key].maxRaw;
}

function clampRaw(value, maxRaw) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.min(maxRaw, Math.max(0, Math.round(n)));
}

function rawForEstimate(value, maxRaw) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.min(maxRaw, Math.max(0, n));
}

function StepperButtons({ label, value, maxRaw, onStep }) {
  return (
    <div className="ucat-score-stepper">
      <button
        type="button"
        className="ucat-score-stepper-btn"
        aria-label={`Increase ${label} raw mark`}
        disabled={value >= maxRaw}
        onClick={() => onStep(1)}
      >
        <svg viewBox="0 0 10 6" aria-hidden="true">
          <path d="M1 5l4-4 4 4" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      <button
        type="button"
        className="ucat-score-stepper-btn"
        aria-label={`Decrease ${label} raw mark`}
        disabled={value <= 0}
        onClick={() => onStep(-1)}
      >
        <svg viewBox="0 0 10 6" aria-hidden="true">
          <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </div>
  );
}

function ScoreRow({
  idKey,
  label,
  maxRaw,
  draft,
  estimateText,
  describedBy,
  help,
  onRangeChange,
  onNumberChange,
  onNumberBlur,
  onStep,
}) {
  const rangeId = `ucat-score-range-${idKey}`;
  const numberId = `ucat-score-raw-${idKey}`;
  const numeric = clampRaw(draft === '' ? 0 : draft, maxRaw);
  const fill = `${(numeric / maxRaw) * 100}%`;

  return (
    <div className="ucat-score-row">
      <div className="ucat-score-row-main">
        <div className="ucat-score-row-meta">
          <label className="ucat-score-section-name" htmlFor={numberId}>
            {label}
          </label>
          <span className="ucat-score-row-estimate">{estimateText}</span>
        </div>
        <input
          id={rangeId}
          className="ucat-score-range"
          type="range"
          min={0}
          max={maxRaw}
          step={1}
          value={numeric}
          onChange={(e) => onRangeChange(idKey, e.target.value)}
          aria-label={`${label} raw marks`}
          aria-describedby={describedBy}
          style={{ '--ucat-score-fill': fill }}
        />
        {help ? (
          <p id={describedBy} className="ucat-score-dm-help">
            {help}
          </p>
        ) : null}
      </div>
      <div className="ucat-score-raw">
        <input
          id={numberId}
          className="ucat-score-raw-input"
          type="number"
          inputMode="numeric"
          min={0}
          max={maxRaw}
          step={1}
          size={3}
          value={draft}
          onChange={(e) => onNumberChange(idKey, e.target.value)}
          onBlur={() => onNumberBlur(idKey)}
          aria-label={`${label} raw mark`}
          aria-describedby={describedBy}
        />
        <StepperButtons
          label={label}
          value={numeric}
          maxRaw={maxRaw}
          onStep={(delta) => onStep(idKey, delta)}
        />
        <span className="ucat-score-denom">/{maxRaw}</span>
      </div>
    </div>
  );
}

function rowEstimateText(key, estimates, sjtBand) {
  if (key === 'sjt') return `Estimated ${sjtBand}`;
  return `Estimated ${estimates.sectionScores[key]}`;
}

function RawMarkEntry({ drafts, estimates, sjtBand, onRangeChange, onNumberChange, onNumberBlur, onStep }) {
  return (
    <article className="ucat-score-panel ucat-score-input">
      <header className="ucat-score-panel-head">
        <h2 className="ucat-score-panel-title">Your mock results</h2>
        <p className="ucat-score-panel-meta">3 cognitive sections</p>
      </header>

      <div className="ucat-score-panel-body">
        {INPUT_ROWS.map((row) => (
          <ScoreRow
            key={row.key}
            idKey={row.key}
            label={row.label}
            maxRaw={row.maxRaw}
            draft={drafts[row.key]}
            estimateText={rowEstimateText(row.key, estimates, sjtBand)}
            describedBy={row.helpId}
            help={row.help}
            onRangeChange={onRangeChange}
            onNumberChange={onNumberChange}
            onNumberBlur={onNumberBlur}
            onStep={onStep}
          />
        ))}

        <div className="ucat-score-notice">
          <svg className="ucat-score-notice-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
            <line x1="12" y1="11" x2="12" y2="16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            <circle cx="12" cy="8" r="0.9" fill="currentColor" />
          </svg>
          <p className="ucat-score-notice-text">
            <strong>Estimate, not an official conversion.</strong> UCAT equates different test forms and does not publish a universal raw-mark table, so your live score may differ.
          </p>
        </div>
      </div>
    </article>
  );
}

function ResultPanel({ estimates, usedRaw, maxRawTotal, sjtBand }) {
  const sjtLabel = sjtBand;

  return (
    <article className="ucat-score-panel ucat-score-results" aria-live="polite">
      <header className="ucat-score-panel-head">
        <h2 className="ucat-score-panel-title">Your estimate</h2>
      </header>
      <div className="ucat-score-panel-body">
        <div className="ucat-score-total">
          <p className="ucat-score-total-kicker">Estimated total score</p>
          <p className="ucat-score-total-line">
            <span className="ucat-score-total-value">{estimates.total.toLocaleString()}</span>
            <span className="ucat-score-total-max">/ {UCAT_TOTAL_MAX.toLocaleString()}</span>
          </p>
          <p className="ucat-score-total-source">
            Based on {usedRaw} of {maxRawTotal} available raw marks
          </p>
        </div>

        <ul className="ucat-score-breakdown">
          {SECTION_KEYS.map((key) => (
            <li key={key} className="ucat-score-breakdown-row">
              <span>{UCAT_SECTION_CONFIG[key].label}</span>
              <span className="ucat-score-breakdown-value">{estimates.sectionScores[key]}</span>
            </li>
          ))}
          <li className="ucat-score-breakdown-row">
            <span>Situational Judgement</span>
            <span className="ucat-score-breakdown-value">{sjtLabel}</span>
          </li>
        </ul>
      </div>
    </article>
  );
}

function UsingThisEstimate() {
  return (
    <article className="ucat-score-panel ucat-score-using">
      <header className="ucat-score-panel-head">
        <h2 className="ucat-score-panel-title">Using this estimate</h2>
        <p className="ucat-score-panel-meta">Practice only</p>
      </header>
      <div className="ucat-score-panel-body ucat-score-copy-body">
        <p>Use the estimate to compare like-for-like mocks and monitor whether your section scores are moving over time.</p>
        <p>It is not a prediction of your live result. Question difficulty and the official UCAT equating process can change the scaled score.</p>
      </div>
    </article>
  );
}

function HowThisEstimateWorks() {
  return (
    <article className="ucat-score-panel ucat-score-how">
      <header className="ucat-score-panel-head">
        <h2 className="ucat-score-panel-title">How this estimate works</h2>
        <p className="ucat-score-panel-meta">Transparent method</p>
      </header>
      <div className="ucat-score-panel-body ucat-score-copy-body">
        <ol className="ucat-score-method-list">
          <li>Raw marks are converted to a 300–900 estimate for each cognitive subtest.</li>
          <li>The three section estimates are added to give a 900–2700 total.</li>
          <li>SJT raw marks out of 69 map to Band 1 (57+), Band 2 (42–56), Band 3 (28–41) or Band 4 (0–27), and are not added to the total.</li>
          <li>The result is a practice estimate, without a percentile or candidate ranking.</li>
        </ol>
        <a
          className="ucat-score-source-link"
          href="https://www.ucat.ac.uk/about-ucat/test-format-and-scoring/"
          target="_blank"
          rel="noopener noreferrer"
        >
          UCAT test format & scoring
        </a>
      </div>
    </article>
  );
}

const UcatScoreCalculator = () => {
  const [raw, setRaw] = useState(DEFAULT_RAW);
  const [drafts, setDrafts] = useState({
    vr: String(DEFAULT_RAW.vr),
    dm: String(DEFAULT_RAW.dm),
    qr: String(DEFAULT_RAW.qr),
    sjt: String(DEFAULT_RAW.sjt),
  });

  const estimates = useMemo(
    () => estimateTotalScore({
      vr: rawForEstimate(raw.vr, UCAT_SECTION_CONFIG.vr.maxRaw),
      dm: rawForEstimate(raw.dm, UCAT_SECTION_CONFIG.dm.maxRaw),
      qr: rawForEstimate(raw.qr, UCAT_SECTION_CONFIG.qr.maxRaw),
    }),
    [raw.vr, raw.dm, raw.qr],
  );

  const sjtBand = useMemo(
    () => estimateSjtBand(rawForEstimate(raw.sjt, UCAT_SJT_MAX_RAW)),
    [raw.sjt],
  );

  const usedRaw = SECTION_KEYS.reduce(
    (sum, key) => sum + clampRaw(raw[key], UCAT_SECTION_CONFIG[key].maxRaw),
    0,
  );
  const maxRawTotal = SECTION_KEYS.reduce((sum, key) => sum + UCAT_SECTION_CONFIG[key].maxRaw, 0);

  const onRangeChange = (key, value) => {
    const next = clampRaw(value, maxFor(key));
    setDrafts((prev) => ({ ...prev, [key]: String(next) }));
    setRaw((prev) => ({ ...prev, [key]: next }));
  };

  const onNumberChange = (key, value) => {
    setDrafts((prev) => ({ ...prev, [key]: value }));
    const parsed = Number(value);
    if (value === '' || !Number.isFinite(parsed)) return;
    setRaw((prev) => ({ ...prev, [key]: parsed }));
  };

  const onNumberBlur = (key) => {
    const next = clampRaw(drafts[key], maxFor(key));
    setDrafts((prev) => ({ ...prev, [key]: String(next) }));
    setRaw((prev) => ({ ...prev, [key]: next }));
  };

  const onStep = (key, delta) => {
    const next = clampRaw(clampRaw(drafts[key], maxFor(key)) + delta, maxFor(key));
    setDrafts((prev) => ({ ...prev, [key]: String(next) }));
    setRaw((prev) => ({ ...prev, [key]: next }));
  };

  return (
    <section className="ucat-ws-page ucat-score-page">
      <div className="ucat-ws-page-header">
        <h1 className="ucat-ws-page-title">UCAT score calculator</h1>
        <p className="ucat-score-subtitle">Turn your mock results into an estimated UCAT score.</p>
      </div>

      <div className="ucat-score-grid">
        <RawMarkEntry
          drafts={drafts}
          sjtBand={sjtBand}
          estimates={estimates}
          onRangeChange={onRangeChange}
          onNumberChange={onNumberChange}
          onNumberBlur={onNumberBlur}
          onStep={onStep}
        />
        <ResultPanel
          estimates={estimates}
          usedRaw={usedRaw}
          maxRawTotal={maxRawTotal}
          sjtBand={sjtBand}
        />
      </div>

      <div className="ucat-score-supporting">
        <UsingThisEstimate />
        <HowThisEstimateWorks />
      </div>
    </section>
  );
};

export default UcatScoreCalculator;
